// ═══════════════════════════════════════════════════════════════════════════
// TOILET MODE UTILITIES - UPDATED WITH TASTEFUL DEGEN ENERGY
// Premium feature for $TOILET token holders
// ═══════════════════════════════════════════════════════════════════════════

// Toilet Token Configuration
export const TOILET_TOKEN_TYPE = '0x361dd589b98f8fcda9a7ee53b85efabef3569d00416640d2faa516e3801d7ffc::TOILET::TOILET';
export const TOILET_TOKEN_THRESHOLD = 287000; // 287,000 $TOILET tokens (~$10)

// Toilet Mode Features
export const TOILET_FEATURES = {
  BONUS_MULTIPLIER: 1.15, // 15% bonus purity earnings
  GAS_DISCOUNT: 0.25, // 25% gas discount
  VISUAL_UPGRADE: true, // Premium toilet circuit visual
  FLUSH_STREAKS: true, // Track consecutive flushes
  EXCLUSIVE_BADGE: true, // "Toilet Mode Active" status
};

// ═══════════════════════════════════════════════════════════════════════════
// BUTTON TEXT - TASTEFUL DEGEN ENERGY 🔥
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get contextual button text based on mode and action
 * @param {string} action - 'purify', 'purifying', or 'purified'
 * @param {boolean} isToiletMode - Whether toilet mode is active
 * @returns {string} Button text with appropriate energy
 */
export const getButtonText = (action, isToiletMode = false) => {
  if (!isToiletMode) {
    // Normal mode - tasteful and professional
    switch (action) {
      case 'purify':
        return 'Purge';
      case 'purifying':
        return 'Purging';
      case 'purified':
        return 'Purged';
      default:
        return 'Purify';
    }
  }

  // Toilet mode - TASTEFUL DEGEN with the legendary button text
  switch (action) {
    case 'purify':
      return "Let's F***ing Flush";
    case 'purifying':
      return 'Flushing';
    case 'purified':
      return 'Flushed';
    default:
      return 'Flush';
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// TOILET MODE DETECTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if user qualifies for Toilet Mode
 * @param {number} toiletBalance - User's $TOILET token balance
 * @param {boolean} isDemoMode - Whether demo toilet mode is manually enabled
 * @returns {boolean} Whether toilet mode should be enabled
 */
export const shouldEnableToiletMode = (toiletBalance, isDemoMode = false) => {
  // Demo mode overrides token balance check
  if (isDemoMode) return true;
  
  // Otherwise check token balance
  return toiletBalance >= TOILET_TOKEN_THRESHOLD;
};

// ═══════════════════════════════════════════════════════════════════════════
// PURITY CALCULATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calculate enhanced purity with toilet mode bonus
 * @param {number} basePurity - Base purity points earned
 * @param {boolean} isToiletMode - Whether toilet mode is active
 * @returns {number} Enhanced purity points
 */
export const calculateEnhancedPurity = (basePurity, isToiletMode = false) => {
  if (!isToiletMode) return basePurity;
  return Math.floor(basePurity * TOILET_FEATURES.BONUS_MULTIPLIER);
};

/**
 * Calculate gas discount for toilet mode users
 * @param {number} baseGas - Base gas cost
 * @param {boolean} isToiletMode - Whether toilet mode is active
 * @returns {number} Discounted gas cost
 */
export const calculateGasDiscount = (baseGas, isToiletMode = false) => {
  if (!isToiletMode) return baseGas;
  return baseGas * (1 - TOILET_FEATURES.GAS_DISCOUNT);
};

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL THEMING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Apply toilet mode theme to the document
 */
export const applyToiletTheme = () => {
  // Apply BOTH classes — dark cosmic is the only scheme
  document.body.classList.add('toilet-mode');
  document.body.classList.add('toilet-bg-normal');

  // Dark cosmic accent vars (teal)
  const style = document.createElement('style');
  style.id = 'toilet-mode-style';
  style.textContent = `
    body.toilet-mode {
      --accent-primary: #00d4c8;
      --accent-secondary: #22d3ee;
      --glow-color: rgba(0, 212, 200, 0.3);
    }
  `;
  document.head.appendChild(style);
};

/**
 * Remove toilet mode theme from the document
 */
export const removeToiletTheme = () => {
  document.body.classList.remove('toilet-mode');
  document.body.classList.remove('toilet-bg-normal');

  const style = document.getElementById('toilet-mode-style');
  if (style) style.remove();
};

// ═══════════════════════════════════════════════════════════════════════════
// FLUSH EFFECTS & ANIMATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Trigger the flush animation effect
 * NO VISUAL ANIMATION - Just sound effect
 */
export const triggerFlushEffect = () => {
  // Just play sound, no spinning toilet overlay
  playFlushSound();
};

/**
 * Play flush sound effect
 */
const playFlushSound = () => {
  try {
    const audio = new Audio('/sounds/flush.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Silently fail if sound not available
    });
  } catch (err) {
    // Silently fail if sound not available
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// FLUSH STREAK TRACKING
// ═══════════════════════════════════════════════════════════════════════════

const FLUSH_STREAK_KEY = 'distil_flush_streak';
const LAST_FLUSH_KEY = 'distil_last_flush';

/**
 * Update flush streak
 * @returns {Object} Streak data { count, lastFlush, milestone }
 */
export const updateFlushStreak = () => {
  const now = Date.now();
  const lastFlush = parseInt(localStorage.getItem(LAST_FLUSH_KEY) || '0');
  const currentStreak = parseInt(localStorage.getItem(FLUSH_STREAK_KEY) || '0');
  
  // Check if last flush was within 24 hours
  const hoursSinceLastFlush = (now - lastFlush) / (1000 * 60 * 60);
  
  let newStreak;
  if (hoursSinceLastFlush <= 24) {
    // Continue streak
    newStreak = currentStreak + 1;
  } else if (hoursSinceLastFlush <= 48) {
    // Grace period - maintain streak
    newStreak = currentStreak + 1;
  } else {
    // Streak broken - start over
    newStreak = 1;
  }
  
  localStorage.setItem(FLUSH_STREAK_KEY, newStreak.toString());
  localStorage.setItem(LAST_FLUSH_KEY, now.toString());
  
  // Check for milestone
  const milestone = checkStreakMilestone(newStreak);
  
  return {
    count: newStreak,
    lastFlush: now,
    milestone,
  };
};

/**
 * Get current flush streak
 * @returns {number} Current streak count
 */
export const getFlushStreak = () => {
  return parseInt(localStorage.getItem(FLUSH_STREAK_KEY) || '0');
};

/**
 * Check if streak hit a milestone
 * @param {number} streak - Current streak count
 * @returns {string|null} Milestone message or null
 */
const checkStreakMilestone = (streak) => {
  const milestones = {
    5: '🔥 5 Flush Streak!',
    10: '💎 10 Flush Streak! You\'re on fire!',
    25: '👑 25 Flush Streak! Legendary!',
    50: '🌟 50 Flush Streak! UNSTOPPABLE!',
    100: '💯 100 FLUSH STREAK! ABSOLUTE LEGEND!',
  };
  
  return milestones[streak] || null;
};

/**
 * Reset flush streak (admin/debug)
 */
export const resetFlushStreak = () => {
  localStorage.removeItem(FLUSH_STREAK_KEY);
  localStorage.removeItem(LAST_FLUSH_KEY);
};

// ═══════════════════════════════════════════════════════════════════════════
// ACHIEVEMENT TRIGGERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check for toilet mode achievements
 * @param {Object} stats - User statistics
 * @returns {Array} Array of unlocked achievements
 */
export const checkToiletAchievements = (stats) => {
  const achievements = [];
  
  // First flush
  if (stats.toiletFlushes === 1) {
    achievements.push({
      id: 'first_flush',
      name: 'First Flush',
      description: 'Activated Toilet Mode for the first time',
      icon: '🚽',
    });
  }
  
  // Flush streak achievements
  if (stats.flushStreak === 7) {
    achievements.push({
      id: 'week_streak',
      name: 'Weekly Flusher',
      description: 'Maintained a 7-day flush streak',
      icon: '📅',
    });
  }
  
  if (stats.flushStreak === 30) {
    achievements.push({
      id: 'month_streak',
      name: 'Monthly Master',
      description: 'Maintained a 30-day flush streak',
      icon: '🗓️',
    });
  }
  
  // Volume achievements
  if (stats.toiletFlushes >= 100) {
    achievements.push({
      id: 'century_flusher',
      name: 'Century Flusher',
      description: 'Completed 100 toilet mode flushes',
      icon: '💯',
    });
  }
  
  return achievements;
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT ALL UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

export default {
  TOILET_TOKEN_TYPE,
  TOILET_TOKEN_THRESHOLD,
  TOILET_FEATURES,
  getButtonText,
  shouldEnableToiletMode,
  calculateEnhancedPurity,
  calculateGasDiscount,
  applyToiletTheme,
  removeToiletTheme,
  triggerFlushEffect,
  updateFlushStreak,
  getFlushStreak,
  resetFlushStreak,
  checkToiletAchievements,
};

// ═══════════════════════════════════════════════════════════════════════════
// TOILET SPINWHEEL PRIZE MANAGEMENT - TASK 6
// ═══════════════════════════════════════════════════════════════════════════

const WHEEL_PRIZE_KEY = 'distil_wheel_prize';

/**
 * Store wheel prize for next flush
 * @param {Object} prize - Prize object from wheel
 */
export const setWheelPrize = (prize) => {
  localStorage.setItem(WHEEL_PRIZE_KEY, JSON.stringify(prize));
};

/**
 * Get current wheel prize
 * @returns {Object|null} Prize object or null
 */
export const getWheelPrize = () => {
  const stored = localStorage.getItem(WHEEL_PRIZE_KEY);
  return stored ? JSON.parse(stored) : null;
};

/**
 * Clear wheel prize after use
 */
export const clearWheelPrize = () => {
  localStorage.removeItem(WHEEL_PRIZE_KEY);
};

/**
 * Apply wheel prize bonuses to purification
 * @param {Object} baseRewards - Base rewards { purity, gas, streak }
 * @param {Object} prize - Prize from wheel
 * @returns {Object} Enhanced rewards
 */
export const applyWheelPrize = (baseRewards, prize) => {
  if (!prize) return baseRewards;

  const enhanced = { ...baseRewards };

  switch (prize.id) {
    case 'legendary':
    case 'epic':
    case 'rare':
    case 'common':
      // Bonus $DISTIL
      enhanced.purity += prize.value;
      break;

    case 'streak':
      // Streak bonus
      enhanced.streakBonus = prize.value;
      break;

    case 'gas':
      // Gas discount
      enhanced.gasDiscount = prize.value;
      break;

    case 'badge':
      // Badge unlock (handled separately)
      enhanced.badgeUnlocked = 'wheel_winner';
      break;
  }

  return enhanced;
};

